import PropTypes from 'prop-types';
import { createContext, useCallback, useContext, useRef, useState } from 'react';

// Shared student data fields (common across Enquiry, Intake, Placement forms)
const initialSharedData = {
  // Child details
  childName: '',
  // What the child is called. Carried across the registration forms so the intake and
  // placement steps show the same name the enquiry captured. Optional throughout — the
  // display helper falls back to the first name.
  preferredName: '',
  childId: '',
  dateOfBirth: '',
  gender: '',

  // Father details
  fatherName: '',
  fatherOccupation: '',
  fatherPlaceOfWork: '',
  fatherMobile: '',
  fatherEmail: '',

  // Mother details
  motherName: '',
  motherOccupation: '',
  motherPlaceOfWork: '',
  motherMobile: '',
  motherEmail: '',

  // Other shared
  languageAtHome: '',
  streetAddress: '',
  city: '',
  state: '',
  zipCode: '',

  // Enquiry-related shared
  primaryConcern: '',
  consultedDoctor: 'no',
  formalAssessments: 'no',
  lookingFor: '',
  currentSchoolProgram: '',

  // Backend IDs (populated after API calls)
  inquiryId: null,
  intakeId: null,
  clientId: null,

  // Enabled datasheets (set in Stage 4)
  enabledDatasheets: null,

  // Pre-registration selections
  selectedCenter: null,
  selectedProgram: null
};

// Field mappings: maps each form's local field name to the shared data field name
// Only fields that differ in name need explicit mapping; same-name fields are auto-mapped
export const FIELD_MAPPINGS = {
  enquiry: {
    // All enquiry fields match shared field names directly
    // source → not shared (enquiry-specific)
    // sourceDetails → not shared
    childName: 'childName',
    preferredName: 'preferredName',
    dateOfBirth: 'dateOfBirth',
    gender: 'gender',
    fatherName: 'fatherName',
    fatherOccupation: 'fatherOccupation',
    fatherPlaceOfWork: 'fatherPlaceOfWork',
    fatherMobile: 'fatherMobile',
    fatherEmail: 'fatherEmail',
    motherName: 'motherName',
    motherOccupation: 'motherOccupation',
    motherPlaceOfWork: 'motherPlaceOfWork',
    motherMobile: 'motherMobile',
    motherEmail: 'motherEmail',
    languageAtHome: 'languageAtHome',
    streetAddress: 'streetAddress',
    city: 'city',
    state: 'state',
    zipCode: 'zipCode',
    primaryConcern: 'primaryConcern',
    consultedDoctor: 'consultedDoctor',
    formalAssessments: 'formalAssessments',
    lookingFor: 'lookingFor',
    currentSchoolProgram: 'currentSchoolProgram'
  },
  intake: {
    childName: 'childName',
    childId: 'childId',
    dateOfBirth: 'dateOfBirth',
    gender: 'gender',
    fatherName: 'fatherName',
    fatherOccupation: 'fatherOccupation',
    fatherMobile: 'fatherMobile',
    fatherEmail: 'fatherEmail',
    motherName: 'motherName',
    motherOccupation: 'motherOccupation',
    motherMobile: 'motherMobile',
    motherEmail: 'motherEmail',
    languageAtHome: 'languageAtHome',
    primaryConcern: 'primaryConcern',
    consultedDoctor: 'consultedDoctor',
    formalAssessments: 'formalAssessments',
    lookingFor: 'lookingFor',
    currentSchoolProgram: 'currentSchoolProgram'
  },
  // IEP — keys on the left are local field names in iep.jsx; values on the
  // right are shared-data field names. Same-name fields still need to be listed.
  // The IEP form's single "address" textarea is seeded from intake's streetAddress;
  // supervisors can edit it inside the IEP without it propagating back.
  iep: {
    childName: 'childName',
    childId: 'childId',
    dateOfBirth: 'dateOfBirth',
    gender: 'gender',
    // Address is the IEP form's single-line address field. The IEP-version
    // editor composes `streetAddress + city + state + zip` into a single
    // `address` value before calling updateSharedData, so we point at that
    // pre-composed key here (falling back to streetAddress alone if address
    // wasn't pre-composed).
    address: 'address',
    parentNames: 'parentNames',
    supervisor: 'supervisor',
    services: 'services'
  },
  placement: {
    // Placement uses different field names
    studentName: 'childName',
    dob: 'dateOfBirth',
    phone: 'fatherMobile'
  },
  placementInteraction: {
    studentName: 'childName'
  },
  sessionNotes: {
    childName: 'childName'
  },
  abcAnalysis: {
    learnerName: 'childName'
  },
  counsellorObservation: {
    studentName: 'childName'
  },
  parentTherapistMeeting: {
    studentName: 'childName',
    parentName: 'fatherName'
  },
  anecdotalRecord: {
    childName: 'childName'
  },
  studentReferral: {
    studentName: 'childName',
    dateOfBirth: 'dateOfBirth',
    contactNumber: 'fatherMobile'
  },
  placementReport: {
    studentName: 'childName',
    gender: 'gender',
    fatherName: 'fatherName',
    motherName: 'motherName',
    dateOfBirth: 'dateOfBirth',
    contactPhone: 'fatherMobile'
  }
};

// Maps the formName a component passes to getSharedFieldsForForm() → the storage
// key it saves its full blob under (saveFormData). Lets getSharedFieldsForForm
// re-hydrate a reopened form with ALL its previously-entered values, not just the
// shared child fields. (Intake saves per-section under dynamic keys, so it's not
// listed here and keeps its own hydration.)
export const FORM_STORAGE_KEYS = {
  enquiry: 'enquiryForm',
  placement: 'placementChecklist',
  placementInteraction: 'placementInteraction',
  placementReport: 'placementReport',
  abcAnalysis: 'abcAnalysis',
  counsellorObservation: 'counsellorObservations',
  sessionNotes: 'sessionNotes',
  anecdotalRecord: 'anecdotalRecord',
  studentReferral: 'studentReferral',
  parentTherapistMeeting: 'parentTherapistMeeting',
  iep: 'iep'
};

// ==============================|| STUDENT REGISTRATION CONTEXT ||============================== //

const StudentRegistrationContext = createContext(null);

export function StudentRegistrationProvider({ children }) {
  const [sharedData, setSharedData] = useState(initialSharedData);
  const [completedForms, setCompletedForms] = useState({});
  const [formDataStore, setFormDataStore] = useState({});
  // Mirror of formDataStore updated SYNCHRONOUSLY so callers can read the latest
  // value in the same tick it was saved (React state + getFormData are a stale
  // closure right after setState — e.g. a form that saveFormData()s then calls
  // onDone() in one handler). getFormDataLive reads this ref.
  const formDataStoreRef = useRef({});
  const [stage1Path, setStage1Path] = useState('direct');
  const [uploadedFiles, setUploadedFiles] = useState({});

  // Update shared data fields
  const updateSharedData = useCallback((fields) => {
    setSharedData((prev) => ({ ...prev, ...fields }));
  }, []);

  // Mark a form as completed
  const markFormCompleted = useCallback((formKey) => {
    setCompletedForms((prev) => ({ ...prev, [formKey]: true }));
  }, []);

  // Optional hook invoked on EVERY saveFormData. The workflow runtime registers
  // one to persist a built-in form's data to the server the moment the form saves
  // it — capturing at save-time avoids all read-back timing/provider pitfalls.
  const onFormSaveRef = useRef(null);
  const registerFormSaveHook = useCallback((fn) => {
    onFormSaveRef.current = typeof fn === 'function' ? fn : null;
  }, []);

  // Store complete form data for PDF download
  const saveFormData = useCallback((formKey, data) => {
    const snapshot = { ...data };
    formDataStoreRef.current = { ...formDataStoreRef.current, [formKey]: snapshot };
    setFormDataStore((prev) => ({ ...prev, [formKey]: snapshot }));
    if (onFormSaveRef.current) {
      try {
        onFormSaveRef.current(formKey, snapshot);
      } catch {
        /* a save hook must never break the form's own save */
      }
    }
  }, []);

  // Get stored form data (from React state — may lag one render after a save)
  const getFormData = useCallback((formKey) => formDataStore[formKey] || null, [formDataStore]);

  // Get stored form data LIVE (from the ref) — always the latest, safe to read
  // immediately after saveFormData in the same tick.
  const getFormDataLive = useCallback((formKey) => formDataStoreRef.current[formKey] || null, []);

  // Drop a single form's cached data. Used when resuming an existing client
  // whose verbatim form blob is missing: clearing the cache lets the form
  // hydrate itself fresh from the backend (and prevents a previously-opened
  // client's cached data from leaking into a different client in the same
  // SPA session). Distinct from resetRegistration, which wipes everything.
  const clearFormData = useCallback((formKey) => {
    if (formKey in formDataStoreRef.current) {
      const nextRef = { ...formDataStoreRef.current };
      delete nextRef[formKey];
      formDataStoreRef.current = nextRef;
    }
    setFormDataStore((prev) => {
      if (!(formKey in prev)) return prev;
      const next = { ...prev };
      delete next[formKey];
      return next;
    });
  }, []);

  // Save uploaded file info
  const saveUploadedFile = useCallback((key, fileInfo) => {
    setUploadedFiles((prev) => ({ ...prev, [key]: fileInfo }));
  }, []);

  // Get uploaded file info
  const getUploadedFile = useCallback((key) => uploadedFiles[key] || null, [uploadedFiles]);

  // Remove uploaded file
  const removeUploadedFile = useCallback((key) => {
    setUploadedFiles((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setCompletedForms((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  // Get shared field values mapped to a form's local field names
  const getSharedFieldsForForm = useCallback(
    (formName) => {
      const result = {};
      const mapping = FIELD_MAPPINGS[formName];
      if (mapping) {
        Object.entries(mapping).forEach(([localField, sharedField]) => {
          if (sharedData[sharedField]) {
            result[localField] = sharedData[sharedField];
          }
        });
      }
      // Re-hydrate the form's OWN saved answers (so reopening restores everything
      // the user entered, not just shared child fields). Saved values override the
      // shared ones. The component spreads this result into its formData on mount.
      const storageKey = FORM_STORAGE_KEYS[formName];
      const saved = storageKey ? formDataStore[storageKey] : null;
      if (saved && typeof saved === 'object') {
        Object.entries(saved).forEach(([k, v]) => {
          if (v !== undefined) result[k] = v;
        });
      }
      return result;
    },
    [sharedData, formDataStore]
  );

  // Tells callers which of a form's local fields are sourced from the Enquiry
  // form (the canonical source of truth). Downstream forms (Intake, IEP,
  // Placement, …) should disable these fields and direct edits back to the
  // Enquiry form so the same value isn't authored in two places.
  //   • For the enquiry form itself, returns an empty Set (nothing locked).
  //   • For everything else, returns the local-field names that appear in
  //     FIELD_MAPPINGS[formName] *and* in FIELD_MAPPINGS.enquiry (i.e. the
  //     shared field is editable on the Enquiry form).
  const getLockedFieldsForForm = useCallback((formName) => {
    if (formName === 'enquiry') return new Set();
    const mapping = FIELD_MAPPINGS[formName];
    if (!mapping) return new Set();
    const enquirySharedFields = new Set(Object.values(FIELD_MAPPINGS.enquiry || {}));
    const locked = new Set();
    Object.entries(mapping).forEach(([localField, sharedField]) => {
      if (enquirySharedFields.has(sharedField)) locked.add(localField);
    });
    return locked;
  }, []);

  // Reset all registration data to start fresh
  const resetRegistration = useCallback(() => {
    setSharedData(initialSharedData);
    setCompletedForms({});
    formDataStoreRef.current = {};
    setFormDataStore({});
    setStage1Path('direct');
    setUploadedFiles({});
  }, []);

  // Bulk-load a student's data for registration (used for dummy/existing students)
  const loadStudentForRegistration = useCallback((studentData) => {
    if (studentData.sharedData) {
      setSharedData((prev) => ({ ...prev, ...studentData.sharedData }));
    }
    if (studentData.completedForms) {
      setCompletedForms(studentData.completedForms);
    }
    if (studentData.formDataStore) {
      formDataStoreRef.current = { ...studentData.formDataStore };
      setFormDataStore(studentData.formDataStore);
    }
    if (studentData.stage1Path) {
      setStage1Path(studentData.stage1Path);
    }
    if (studentData.uploadedFiles) {
      setUploadedFiles(studentData.uploadedFiles);
    }
  }, []);

  // Push form data back to shared context (only mapped fields)
  const syncFormToShared = useCallback(
    (formName, formData) => {
      const mapping = FIELD_MAPPINGS[formName];
      if (!mapping) return;

      const updates = {};
      Object.entries(mapping).forEach(([localField, sharedField]) => {
        if (formData[localField] !== undefined && formData[localField] !== '') {
          updates[sharedField] = formData[localField];
        }
      });
      if (Object.keys(updates).length > 0) {
        updateSharedData(updates);
      }
    },
    [updateSharedData]
  );

  return (
    <StudentRegistrationContext.Provider
      value={{
        sharedData,
        completedForms,
        formDataStore,
        uploadedFiles,
        stage1Path,
        setStage1Path,
        updateSharedData,
        markFormCompleted,
        saveFormData,
        getFormData,
        getFormDataLive,
        registerFormSaveHook,
        clearFormData,
        saveUploadedFile,
        getUploadedFile,
        removeUploadedFile,
        getSharedFieldsForForm,
        getLockedFieldsForForm,
        syncFormToShared,
        loadStudentForRegistration,
        resetRegistration
      }}
    >
      {children}
    </StudentRegistrationContext.Provider>
  );
}

StudentRegistrationProvider.propTypes = {
  children: PropTypes.node
};

// Custom hook
export function useStudentRegistration() {
  const context = useContext(StudentRegistrationContext);
  if (!context) {
    throw new Error('useStudentRegistration must be used within a StudentRegistrationProvider');
  }
  return context;
}

export default StudentRegistrationContext;
